import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Download, FileText, Folder, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { botsApi } from '../../services/api';
import { useWorkspaceFilesQuery } from '../../hooks/queries';
import { saveFile } from '../../native/files';

// Byte units in the viewer's language ("kB", "ko"), from Intl.
const UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte'];
const size = (n, lang) => {
  if (n == null) return '';
  let i = 0; let v = n;
  while (v >= 1024 && i < UNITS.length - 1) { v /= 1024; i += 1; }
  const digits = { maximumFractionDigits: i ? 1 : 0 };
  try {
    return new Intl.NumberFormat(lang, { style: 'unit', unit: UNITS[i], unitDisplay: 'short', ...digits }).format(v);
  } catch {
    return `${new Intl.NumberFormat(lang, digits).format(v)} ${['B', 'KB', 'MB', 'GB'][i]}`;
  }
};

/** What the bots saved on the project's computer, to browse and download. */
const WorkspaceFiles = ({ projectId, open, onClose }) => {
  const { t, i18n } = useTranslation();
  const [path, setPath] = useState('');
  const { data, isLoading, isError, error } = useWorkspaceFilesQuery(projectId, path, { enabled: open });
  const [busy, setBusy] = useState(null);
  const crumbs = path ? path.split('/') : [];

  const download = async (entry) => {
    setBusy(entry.path);
    try {
      const res = await botsApi.workspaceDownload(projectId, entry.path);
      await saveFile(res.data, entry.name);
    } catch {
      toast.error(t('bots.desktop.download_failed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('bots.desktop.files')}</DialogTitle>
          <DialogDescription className="flex items-center gap-1 flex-wrap text-xs">
            <button type="button" className="hover:underline" onClick={() => setPath('')}>{t('bots.desktop.files_root')}</button>
            {crumbs.map((c, i) => (
              <React.Fragment key={i}>
                <ChevronRight className="w-3 h-3" />
                <button type="button" className="hover:underline" onClick={() => setPath(crumbs.slice(0, i + 1).join('/'))}>{c}</button>
              </React.Fragment>
            ))}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-2" data-testid="workspace-files">
          {isLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : isError ? (
            <p className="px-2 py-6 text-sm text-muted-foreground text-center">
              {error?.response?.data?.detail || t('bots.desktop.failed')}
            </p>
          ) : (data?.entries || []).length === 0 ? (
            <p className="px-2 py-6 text-sm text-muted-foreground text-center">{t('bots.desktop.files_empty')}</p>
          ) : (
            <ul>
              {data.entries.map((e) => (
                <li key={e.path}>
                  <button type="button" onClick={() => (e.folder ? setPath(e.path) : download(e))}
                    className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-accent text-left text-sm">
                    {e.folder ? <Folder className="w-4 h-4 text-amber-500 shrink-0" /> : <FileText className="w-4 h-4 text-muted-foreground shrink-0" />}
                    <span className="flex-1 truncate">{e.name}</span>
                    {!e.folder && <span className="text-xs text-muted-foreground tabular-nums">{size(e.size, i18n.language)}</span>}
                    {!e.folder && (busy === e.path
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                      : <Download className="w-3.5 h-3.5 text-muted-foreground" />)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkspaceFiles;
